import React from 'react'
import { Button } from '../ui/Button'
import { useI18n } from '../../i18n'
import { useAuth } from '../AuthProvider'
import { getActiveOrgId } from '../../utils/orgUtils'
import { executeBlockerAction, fetchReadinessWithActions } from '../../services/informationSystemPhase16Api'

interface Props {
  systemId: string
}

export default function ReadinessBlockersPanel({ systemId }: Props) {
  const { t } = useI18n()
  const { token } = useAuth()
  const orgId = getActiveOrgId()
  const [data, setData] = React.useState<Record<string, unknown> | null>(null)
  const [loading, setLoading] = React.useState(false)
  const [acting, setActing] = React.useState<string | null>(null)

  const load = React.useCallback(async () => {
    if (!token || !systemId) return
    setLoading(true)
    try {
      const res = await fetchReadinessWithActions(token, systemId, orgId)
      setData(res)
    } catch {
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [token, systemId, orgId])

  React.useEffect(() => {
    load()
  }, [load])

  const blockers = (data?.blockers as Array<Record<string, unknown>>) || []

  const onAction = async (code: string) => {
    if (!token) return
    setActing(code)
    try {
      await executeBlockerAction(token, systemId, code, orgId)
      await load()
    } finally {
      setActing(null)
    }
  }

  if (loading) return <p className="text-sm text-gray-500">{t('common.loading', 'Loading…')}</p>
  if (!blockers.length) return null

  return (
    <section className="rounded-lg border border-amber-200 bg-amber-50/50 p-3 space-y-2">
      <h4 className="font-semibold text-amber-900 text-sm">
        {t('assets.workflows.blockersTitle', 'Readiness blockers')}
      </h4>
      <ul className="space-y-2">
        {blockers.map((b) => (
          <li key={String(b.code)} className="text-sm flex flex-wrap items-start justify-between gap-2">
            <div>
              <p className="font-medium text-gray-900">{String(b.message)}</p>
              {b.suggestedAction ? (
                <p className="text-xs text-gray-600">
                  {t('assets.workflows.suggestedAction', 'Action')}: {String(b.suggestedAction)}
                </p>
              ) : null}
            </div>
            {b.actionable ? (
              <Button
                type="button"
                size="small"
                disabled={acting === String(b.code)}
                onClick={() => onAction(String(b.code))}
              >
                {t('assets.workflows.runAction', 'Run workflow')}
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  )
}
